import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../App";
import {
  LayoutDashboard,
  FolderKanban,
  Wallet,
  TrendingUp,
  Shield,
  DollarSign,
  Users,
  Map,
  Building2,
  Contact,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Bell,
  Search,
  Menu,
  FolderPlus,
  Target
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const navItems = [
  { path: "/", label: "Executive Dashboard", icon: LayoutDashboard },
  { path: "/pmo", label: "PMO Operations", icon: FolderKanban },
  { path: "/project-management", label: "Project Management", icon: FolderPlus },
  { path: "/project-score", label: "Project Score", icon: Target },
  { path: "/funds", label: "M1 Funds & Liquidity", icon: Wallet },
  { path: "/trade", label: "Trade Performance", icon: TrendingUp },
  { path: "/governance", label: "Governance & Audit", icon: Shield },
  { path: "/financial", label: "Financial Management", icon: DollarSign },
  { path: "/funder", label: "Funder Dashboard", icon: Users },
  { path: "/regions", label: "African Regions", icon: Map },
  { path: "/vendors", label: "Vendors", icon: Building2 },
  { path: "/crm", label: "CRM", icon: Contact },
];

const DashboardLayout = ({ children }) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#0b1c2d]" data-testid="dashboard-layout">
      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-0 h-full bg-slate-950/90 backdrop-blur-xl border-r border-white/5 z-40 transition-all duration-300 ${
          sidebarCollapsed ? "w-20" : "w-64"
        } hidden lg:block`}
      >
        {/* Logo */}
        <div className="h-16 flex items-center px-4 border-b border-white/5">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-sm bg-[#c9a227] flex items-center justify-center">
              <span className="text-[#0b1c2d] font-bold text-lg">CD</span>
            </div>
            {!sidebarCollapsed && (
              <div>
                <h1 className="text-white font-semibold text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  CDACE–ClemTech
                </h1>
                <p className="text-slate-500 text-xs">PGMO Platform</p>
              </div>
            )}
          </Link>
        </div>

        {/* Navigation */}
        <nav className="p-3 space-y-1 overflow-y-auto h-[calc(100vh-8rem)]">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.path.replace("/", "") || "home"}`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-sm transition-all duration-200 ${
                  isActive
                    ? "text-[#c9a227] bg-[#c9a227]/10 border-l-2 border-[#c9a227]"
                    : "text-slate-400 hover:text-white hover:bg-white/5"
                }`}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                {!sidebarCollapsed && (
                  <span className="text-sm truncate">{item.label}</span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Collapse Toggle */}
        <button
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          className="absolute -right-3 top-20 w-6 h-6 bg-slate-800 border border-slate-700 rounded-full flex items-center justify-center text-slate-400 hover:text-white"
        >
          {sidebarCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </button>
      </aside>

      {/* Mobile Sidebar */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="fixed inset-0 bg-black/50" onClick={() => setMobileMenuOpen(false)} />
          <aside className="fixed left-0 top-0 h-full w-64 bg-slate-950/95 backdrop-blur-xl border-r border-white/5">
            <div className="h-16 flex items-center justify-between px-4 border-b border-white/5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-sm bg-[#c9a227] flex items-center justify-center">
                  <span className="text-[#0b1c2d] font-bold text-lg">CD</span>
                </div>
                <div>
                  <h1 className="text-white font-semibold text-sm">CDACE–ClemTech</h1>
                  <p className="text-slate-500 text-xs">PGMO Platform</p>
                </div>
              </div>
            </div>
            <nav className="p-3 space-y-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-sm transition-all ${
                      isActive
                        ? "text-[#c9a227] bg-[#c9a227]/10"
                        : "text-slate-400 hover:text-white hover:bg-white/5"
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="text-sm">{item.label}</span>
                  </Link>
                );
              })}
            </nav>
          </aside>
        </div>
      )}

      {/* Main Content */}
      <div className={`transition-all duration-300 ${sidebarCollapsed ? "lg:ml-20" : "lg:ml-64"}`}>
        {/* Top Bar */}
        <header className="sticky top-0 h-16 bg-slate-950/80 backdrop-blur-md border-b border-white/5 z-30 flex items-center justify-between px-4 lg:px-6">
          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(true)}
            className="lg:hidden text-slate-400 hover:text-white"
          >
            <Menu className="w-6 h-6" />
          </button>

          {/* Search */}
          <div className="hidden md:flex items-center gap-2 flex-1 max-w-md">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                type="text"
                placeholder="Search projects, funds, vendors..."
                className="pl-9 bg-slate-900/50 border-slate-700 focus:border-[#c9a227] text-sm w-full"
              />
            </div>
          </div>

          {/* Right Section */}
          <div className="flex items-center gap-4">
            {/* Notifications */}
            <button className="relative text-slate-400 hover:text-white">
              <Bell className="w-5 h-5" />
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#c9a227] rounded-full text-[10px] text-[#0b1c2d] font-bold flex items-center justify-center">
                3
              </span>
            </button>

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 text-slate-300 hover:text-white" data-testid="user-menu-trigger">
                  <div className="w-8 h-8 rounded-sm bg-[#c9a227]/20 border border-[#c9a227]/30 flex items-center justify-center">
                    <span className="text-[#c9a227] text-sm font-semibold">
                      {user?.name?.charAt(0) || "U"}
                    </span>
                  </div>
                  <div className="hidden sm:block text-left">
                    <p className="text-sm font-medium">{user?.name || "User"}</p>
                    <p className="text-xs text-slate-500 capitalize">{user?.role || "Role"}</p>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 bg-slate-900 border-slate-700">
                <DropdownMenuLabel className="text-slate-400">My Account</DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-slate-700" />
                <DropdownMenuItem className="text-slate-300 focus:text-white focus:bg-slate-800">
                  Profile Settings
                </DropdownMenuItem>
                <DropdownMenuItem className="text-slate-300 focus:text-white focus:bg-slate-800">
                  Preferences
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-slate-700" />
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="text-red-400 focus:text-red-300 focus:bg-slate-800"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 lg:p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
